import { useState, useEffect, useRef, useCallback } from 'react';
import './AdminPage.css';
import { API_URL as API } from '../config/env.js';
import StoryExcerpt from '../components/StoryExcerpt.jsx';
import { excerptCardPreview } from '../utils/storyText.js';
import { adminHeaders, adminAuthErrorMessage } from '../utils/adminAuth.js';
import AdminSubscribers from '../components/admin/AdminSubscribers.jsx';
import AdminPayments from '../components/admin/AdminPayments.jsx';
import AdminSiteSettings from '../components/admin/AdminSiteSettings.jsx';
import { getAdImageSpec, adImageUploadHint } from '../config/adSpecs.js';
import { todayIST, maxScheduleIST, SCHEDULE_HORIZON_DAYS, isFutureEdition } from '../utils/dateIST.js';
import {
  STORY_CATEGORIES,
  resolveStoryCategoryId,
  storyCategoryFields,
  storyCategoryLabel,
} from '../config/storyCategories.js';

const MAX_STORY_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_AD_VIDEO_BYTES = 50 * 1024 * 1024;

const EMPTY_AD_FORM = {
  placement: 'sidebar',
  mediaType: 'image',
  videoUrl: '',
  title: '',
  titleEn: '',
  content: '',
  contentEn: '',
  buttonLabel: '',
  buttonLabelEn: '',
  buttonUrl: '',
  sponsorLabel: '',
  active: true,
};

const DEFAULT_STORY_CAT = storyCategoryFields('politics');

const EMPTY_STORY_FORM = {
  title: '',
  titleEn: '',
  excerpt: '',
  excerptEn: '',
  body: '',
  bodyEn: '',
  category: DEFAULT_STORY_CAT.category,
  categoryEn: DEFAULT_STORY_CAT.categoryEn,
  categoryId: 'politics',
  color: '#1565c0',
  featured: false,
  readerLayout: 'landscape',
  cardAspect: 'landscape',
  publishAt: todayIST(),
};

const STORY_CARD_ASPECT_HINTS = {
  landscape: 'Recommended: 1200 × 675 px (16:9) — wide photo for homepage cards',
  portrait: 'Recommended: 900 × 1200 px (3:4) — tall photo for homepage cards',
  square: 'Recommended: 1000 × 1000 px (1:1) — square photo for homepage cards',
};
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const NAV = [
  { id: 'overview', label: 'Dashboard', icon: '◉', group: 'home' },
  { id: 'subscribers', label: 'Subscribers', icon: '👥', group: 'readers' },
  { id: 'payments', label: 'Payments', icon: '💳', group: 'readers' },
  { id: 'papers', label: 'Daily Edition', icon: '📰', group: 'editions' },
  { id: 'monthly', label: 'Monthly Magazine', icon: '📖', group: 'editions' },
  { id: 'stories', label: 'Exclusive Stories', icon: '✦', group: 'homepage' },
  { id: 'ads', label: 'Advertisements', icon: '◎', group: 'homepage' },
  { id: 'poll', label: 'Poll', icon: '◧', group: 'homepage' },
  { id: 'breaking', label: 'Breaking News', icon: '⚡', group: 'homepage' },
  { id: 'site', label: 'Site Settings', icon: '⚙', group: 'website' },
];

const NAV_GROUPS = [
  { id: 'home', label: 'Home' },
  { id: 'readers', label: 'Readers & billing' },
  { id: 'editions', label: 'Editions' },
  { id: 'homepage', label: 'Homepage content' },
  { id: 'website', label: 'Website' },
];

const SECTION_COPY = {
  overview: {
    title: 'Dashboard',
    desc: 'At-a-glance counts and shortcuts to every section of your admin panel.',
  },
  subscribers: {
    title: 'Subscribers & billing',
    desc: 'Readers, unlock tools, and GST tax invoices for filing.',
  },
  payments: {
    title: 'Payments',
    desc: 'Razorpay orders — revenue, pending, refunds, and payment IDs.',
  },
  site: {
    title: 'Site Settings',
    desc: 'LIVE badge, homepage banner, reader WhatsApp help, and team shortcuts.',
  },
  papers: {
    title: 'Daily Edition',
    desc: 'Upload one PDF per day. Each date replaces the previous file for that day.',
  },
  monthly: {
    title: 'Monthly Magazine',
    desc: 'Upload one PDF per calendar month. Separate from daily editions.',
  },
  stories: {
    title: 'Exclusive Stories',
    desc: 'Publish or schedule exclusive stories (same date rules as daily paper). Featured carousel, image size, reader layout, and drag-to-reorder.',
  },
  ads: {
    title: 'Advertisements',
    desc: 'Sidebar (720×405px, 16:9) and top partner banner (640×144px wide logo). Image, MP4, or video URL, optional CTA.',
  },
  poll: {
    title: 'Poll',
    desc: 'Set the active poll question and answer options visitors can vote on.',
  },
  breaking: {
    title: 'Breaking News',
    desc: 'Headlines shown in the ticker at the top of the website.',
  },
};

async function parseApiJson(res) {
  const raw = await res.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return { error: res.status >= 500 ? `Server error (${res.status})` : 'Invalid server response' };
  }
}

function formatDateEn(dk) {
  const [y, m, d] = dk.split('-');
  return `${MONTHS_EN[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

function formatMonthEn(mk) {
  const [y, mo] = mk.split('-');
  return `${MONTHS_EN[parseInt(mo, 10) - 1]} ${y}`;
}

function UploadDrop({ label, hint, accept, file, onPick, inputRef, icon = '📄' }) {
  const [drag, setDrag] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onPick(f);
  };

  return (
    <div className="adm-field">
      <label>{label}</label>
      <div
        className={`adm-drop ${file ? 'has-file' : ''} ${drag ? 'drag-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
      >
        <div className="adm-drop-icon">{icon}</div>
        <div className="adm-drop-title">
          {file ? file.name : 'Drag & drop or click to browse'}
        </div>
        <div className="adm-drop-hint">{hint}</div>
        {file && (
          <div className="adm-drop-file">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
    </div>
  );
}

export default function AdminPage() {
  const [screen, setScreen] = useState('login');
  const [token, setToken] = useState(() => localStorage.getItem('rk_admin_token') || '');
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [loginErr, setLoginErr] = useState('');

  const [papers, setPapers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [file, setFile] = useState(null);
  const [date, setDate] = useState(() => todayIST());
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [apnsConfigured, setApnsConfigured] = useState(false);
  const [notifyingDate, setNotifyingDate] = useState(null);
  const [lastUploadMeta, setLastUploadMeta] = useState(null);
  const fileRef = useRef();

  const [monthlyPapers, setMonthlyPapers] = useState([]);
  const [monthlyUploading, setMonthlyUploading] = useState(false);
  const [monthlyDeleting, setMonthlyDeleting] = useState(null);
  const [monthlyFile, setMonthlyFile] = useState(null);
  const [monthKey, setMonthKey] = useState(new Date().toISOString().slice(0, 7));
  const monthlyFileRef = useRef();

  const [stories, setStories] = useState([]);
  const [orderedStories, setOrderedStories] = useState([]);
  const [savingStory, setSavingStory] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deletingStory, setDeletingStory] = useState(null);
  const [editingStoryId, setEditingStoryId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const imageRef = useRef();
  const dragStoryId = useRef(null);
  const [storyForm, setStoryForm] = useState({ ...EMPTY_STORY_FORM });

  const [ads, setAds] = useState([]);
  const [orderedSidebarAds, setOrderedSidebarAds] = useState([]);
  const [orderedBannerAds, setOrderedBannerAds] = useState([]);
  const [savingAd, setSavingAd] = useState(false);
  const [deletingAd, setDeletingAd] = useState(null);
  const [editingAdId, setEditingAdId] = useState(null);
  const [adForm, setAdForm] = useState({ ...EMPTY_AD_FORM });
  const [adImageFile, setAdImageFile] = useState(null);
  const [adImagePreview, setAdImagePreview] = useState('');
  const adImageRef = useRef(null);
  const [adVideoFile, setAdVideoFile] = useState(null);
  const [adVideoPreview, setAdVideoPreview] = useState('');
  const adVideoRef = useRef(null);
  const dragAdId = useRef(null);

  const [pollQ, setPollQ] = useState('');
  const [pollOpts, setPollOpts] = useState(['', '', '', '']);
  const [currentPoll, setCurrentPoll] = useState(null);
  const [pollLoading, setPollLoading] = useState(false);
  const [savingPoll, setSavingPoll] = useState(false);

  const [breakingNews, setBreakingNews] = useState([]);
  const [newHeadline, setNewHeadline] = useState('');
  const [savingHeadline, setSavingHeadline] = useState(false);
  const [deletingHeadline, setDeletingHeadline] = useState(null);

  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => { if (token) setScreen('dashboard'); }, [token]);

  useEffect(() => {
    document.body.classList.toggle('adm-nav-lock', navOpen);
    return () => document.body.classList.remove('adm-nav-lock');
  }, [navOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1024) setNavOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const goToTab = (id) => {
    setTab(id);
    setNavOpen(false);
  };

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && NAV.some((n) => n.id === hash)) setTab(hash);
  }, []);

  useEffect(() => {
    if (screen === 'dashboard') {
      window.history.replaceState(null, '', `#${tab}`);
    }
  }, [tab, screen]);

  useEffect(() => {
    if (screen === 'dashboard') {
      fetchPapers();
      fetchMonthlyPapers();
      fetchStories();
      fetchAds();
      fetchBreakingNews();
      fetchRegisteredUsers();
    }
  }, [screen]);

  useEffect(() => {
    if (screen === 'dashboard' && tab === 'poll') fetchPoll();
  }, [screen, tab]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const logoutAdmin = useCallback((msg = 'Session expired. Please log in again.') => {
    localStorage.removeItem('rk_admin_token');
    setToken('');
    setScreen('login');
    showToast(msg, 'error');
  }, [showToast]);

  async function fetchPapers() {
    try {
      const res = await fetch(`${API}/api/admin/papers`, { headers: adminHeaders(token) });
      if (res.status === 401) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const pub = await fetch(`${API}/api/papers`);
        const pubData = await pub.json().catch(() => ({}));
        setPapers(pubData.dates || []);
        return;
      }
      setPapers(data.dates || []);
      setWhatsappConfigured(!!data.whatsappConfigured);
      setApnsConfigured(!!data.apnsConfigured);
    } catch { /* ignore */ }
  }

  async function handleNotifyWhatsApp(editionDate, { force = false } = {}) {
    if (!editionDate) return;
    if (isFutureEdition(editionDate)) {
      return showToast('WhatsApp notify is only for today’s (or past) live edition', 'error');
    }
    setNotifyingDate(editionDate);
    try {
      const res = await fetch(`${API}/api/admin/notify-paper-live`, {
        method: 'POST',
        headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: editionDate, force }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (!res.ok) {
        showToast(data.error || 'WhatsApp notify failed', 'error');
      } else if (!data.configured) {
        const pushNote = data.push
          ? ` · Push: sent ${data.push.sent || 0}${data.push.skipped ? ' (already sent)' : ''}`
          : '';
        showToast((data.error || 'WhatsApp Cloud API not configured on server') + pushNote, 'error');
      } else {
        const pushNote = data.push
          ? ` · Push: sent ${data.push.sent || 0}, failed ${data.push.failed || 0}${data.push.skipped ? ' (already sent)' : ''}`
          : data.apnsConfigured === false
            ? ' · Push: APNs not configured'
            : '';
        const why = data.failed && data.error ? ` — ${data.error}` : '';
        showToast(
          `WhatsApp: sent ${data.sent || 0}, failed ${data.failed || 0}, skipped ${data.skipped || 0}${why}${pushNote}`,
          data.failed ? 'error' : 'success'
        );
      }
    } catch {
      showToast('WhatsApp notify network error', 'error');
    }
    setNotifyingDate(null);
  }

  async function fetchMonthlyPapers() {
    try {
      const res = await fetch(`${API}/api/monthly-papers`);
      const data = await res.json();
      setMonthlyPapers(data.months || []);
    } catch { /* ignore */ }
  }

  async function fetchStories() {
    try {
      const res = await fetch(`${API}/api/admin/stories`, {
        headers: adminHeaders(token),
      });
      if (res.status === 401) {
        const d = await res.json().catch(() => ({}));
        return logoutAdmin(adminAuthErrorMessage(res, d));
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setStories(list);
      setOrderedStories(
        [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      );
    } catch { /* ignore */ }
  }

  function resetStoryForm() {
    setEditingStoryId(null);
    setStoryForm({ ...EMPTY_STORY_FORM, publishAt: todayIST() });
    setImageFile(null);
    setImagePreview('');
    if (imageRef.current) imageRef.current.value = '';
  }

  function startEditStory(s) {
    setEditingStoryId(s.id);
    const catId = resolveStoryCategoryId(s) || 'politics';
    const fields = storyCategoryFields(catId);
    setStoryForm({
      title: s.title || '',
      titleEn: s.titleEn || '',
      excerpt: s.excerpt || '',
      excerptEn: s.excerptEn || '',
      body: s.body || s.excerpt || '',
      bodyEn: s.bodyEn || s.excerptEn || '',
      category: fields.category,
      categoryEn: fields.categoryEn,
      categoryId: catId,
      color: s.color || '#1565c0',
      featured: !!s.featured,
      readerLayout: s.readerLayout || 'landscape',
      cardAspect: s.cardAspect || 'landscape',
      publishAt: s.publishAt || todayIST(),
    });
    setImageFile(null);
    const img = s.image?.startsWith('http') ? s.image : `${API}${s.image}`;
    setImagePreview(img);
    if (imageRef.current) imageRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function fillCardSummaryFromBody(langKey) {
    const source = langKey === 'en' ? storyForm.bodyEn || storyForm.body : storyForm.body;
    const summary = excerptCardPreview(source, 200);
    if (!summary) return showToast('Add article text first', 'error');
    if (langKey === 'en') {
      setStoryForm((f) => ({ ...f, excerptEn: summary }));
    } else {
      setStoryForm((f) => ({ ...f, excerpt: summary }));
    }
    showToast('Card summary updated from article');
  }

  function reorderStoriesList(fromId, toId) {
    if (!fromId || !toId || fromId === toId) return null;
    const next = [...orderedStories];
    const fromIdx = next.findIndex((s) => s.id === fromId);
    const toIdx = next.findIndex((s) => s.id === toId);
    if (fromIdx < 0 || toIdx < 0) return null;
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    setOrderedStories(next);
    return next;
  }

  async function persistStoryOrder(orderedList) {
    setSavingOrder(true);
    try {
      const res = await fetch(`${API}/api/admin/stories/reorder`, {
        method: 'PUT',
        headers: adminHeaders(token, { json: true }),
        body: JSON.stringify({ order: orderedList.map((s) => s.id) }),
      });
      const data = await parseApiJson(res);
      if (res.ok) {
        showToast('Story order saved');
        fetchStories();
      } else {
        showToast(data.error || 'Could not save order', 'error');
        fetchStories();
      }
    } catch {
      showToast('Network error', 'error');
      fetchStories();
    }
    setSavingOrder(false);
  }

  async function fetchAds() {
    try {
      const res = await fetch(`${API}/api/admin/ads`, {
        headers: adminHeaders(token),
      });
      if (res.status === 401) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setAds(list);
      setOrderedSidebarAds(
        list.filter((a) => a.placement === 'sidebar').sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      );
      setOrderedBannerAds(
        list.filter((a) => a.placement === 'banner').sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      );
    } catch { /* ignore */ }
  }

  function resetAdForm() {
    setEditingAdId(null);
    setAdForm({ ...EMPTY_AD_FORM });
    setAdImageFile(null);
    setAdImagePreview('');
    setAdVideoFile(null);
    setAdVideoPreview('');
    if (adImageRef.current) adImageRef.current.value = '';
    if (adVideoRef.current) adVideoRef.current.value = '';
  }

  function startEditAd(ad) {
    setEditingAdId(ad.id);
    const hostedVideo =
      ad.mediaType === 'video' && ad.videoUrl?.startsWith('/ads/media/');
    setAdForm({
      placement: ad.placement || 'sidebar',
      mediaType: ad.mediaType || 'image',
      videoUrl: hostedVideo ? '' : (ad.videoUrl || ''),
      title: ad.title || '',
      titleEn: ad.titleEn || '',
      content: ad.content || '',
      contentEn: ad.contentEn || '',
      buttonLabel: ad.buttonLabel || '',
      buttonLabelEn: ad.buttonLabelEn || '',
      buttonUrl: ad.buttonUrl || '',
      sponsorLabel: ad.sponsorLabel || '',
      active: ad.active !== false,
    });
    setAdImageFile(null);
    setAdVideoFile(null);
    if (ad.image) {
      const img = ad.image.startsWith('http') ? ad.image : `${API}${ad.image}`;
      setAdImagePreview(img);
    } else {
      setAdImagePreview('');
    }
    if (hostedVideo) {
      const vid = ad.videoUrl.startsWith('http') ? ad.videoUrl : `${API}${ad.videoUrl}`;
      setAdVideoPreview(vid);
    } else if (adVideoFile) {
      setAdVideoPreview(URL.createObjectURL(adVideoFile));
    } else {
      setAdVideoPreview('');
    }
    if (adVideoRef.current) adVideoRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function reorderAdList(list, fromId, toId) {
    if (!fromId || !toId || fromId === toId) return null;
    const next = [...list];
    const fromIdx = next.findIndex((a) => a.id === fromId);
    const toIdx = next.findIndex((a) => a.id === toId);
    if (fromIdx < 0 || toIdx < 0) return null;
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    return next;
  }

  async function persistAdOrder(orderedList, placement) {
    try {
      const res = await fetch(`${API}/api/admin/ads/reorder`, {
        method: 'PUT',
        headers: adminHeaders(token, { json: true }),
        body: JSON.stringify({ order: orderedList.map((a) => a.id), placement }),
      });
      const data = await parseApiJson(res);
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (res.ok) {
        showToast('Ad order saved');
        fetchAds();
      } else {
        showToast(data.error || 'Could not save order', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    }
  }

  async function handleSaveAd(e) {
    e.preventDefault();
    if (adForm.mediaType === 'image' && !editingAdId && !adImageFile) {
      return showToast('Upload an image for image ads', 'error');
    }
    const existingAd = editingAdId ? ads.find((a) => a.id === editingAdId) : null;
    const hasHostedVideo =
      adForm.mediaType === 'video'
      && existingAd?.videoUrl?.startsWith('/ads/media/')
      && !adVideoFile
      && !adForm.videoUrl.trim();
    if (adForm.mediaType === 'video' && !adForm.videoUrl.trim() && !adVideoFile && !hasHostedVideo) {
      return showToast('Upload an MP4 video or paste a YouTube / Facebook / MP4 link', 'error');
    }

    setSavingAd(true);
    try {
      const fd = new FormData();
      fd.append('placement', adForm.placement);
      fd.append('mediaType', adForm.mediaType);
      fd.append('videoUrl', adForm.mediaType === 'video' ? (adForm.videoUrl || '').trim() : '');
      fd.append('title', adForm.title);
      fd.append('titleEn', adForm.titleEn);
      fd.append('content', adForm.content);
      fd.append('contentEn', adForm.contentEn);
      fd.append('buttonLabel', adForm.buttonLabel);
      fd.append('buttonLabelEn', adForm.buttonLabelEn);
      fd.append('buttonUrl', adForm.buttonUrl);
      fd.append('sponsorLabel', adForm.sponsorLabel);
      fd.append('active', adForm.active ? '1' : '0');
      if (adImageFile) fd.append('image', adImageFile, adImageFile.name || 'ad.jpg');
      if (adVideoFile) fd.append('video', adVideoFile, adVideoFile.name || 'ad.mp4');

      const url = editingAdId ? `${API}/api/admin/ads/${editingAdId}` : `${API}/api/admin/ads`;
      const res = await fetch(url, {
        method: editingAdId ? 'PUT' : 'POST',
        headers: adminHeaders(token),
        body: fd,
      });
      const data = await parseApiJson(res);
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (res.ok && data.success) {
        showToast(editingAdId ? 'Advertisement updated' : 'Advertisement published');
        resetAdForm();
        fetchAds();
      } else {
        showToast(data.error || 'Save failed', 'error');
      }
    } catch {
      showToast('Network error', 'error');
    }
    setSavingAd(false);
  }

  async function handleDeleteAd(id) {
    if (!window.confirm('Delete this advertisement?')) return;
    setDeletingAd(id);
    const res = await fetch(`${API}/api/admin/ads/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(token),
    });
    if (res.status === 401) {
      const d = await res.json().catch(() => ({}));
      setDeletingAd(null);
      return logoutAdmin(adminAuthErrorMessage(res, d));
    }
    setDeletingAd(null);
    if (editingAdId === id) resetAdForm();
    fetchAds();
    showToast('Advertisement removed');
  }

  async function fetchBreakingNews() {
    try {
      const res = await fetch(`${API}/api/breaking-news`);
      setBreakingNews(await res.json());
    } catch { /* ignore */ }
  }

  async function fetchRegisteredUsers() {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/users`, { headers: adminHeaders(token) });
      if (res.status === 401) return;
      const data = await res.json();
      setRegisteredUsers(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }

  async function fetchPoll() {
    setPollLoading(true);
    try {
      const res = await fetch(`${API}/api/poll`);
      if (res.ok) {
        const data = await res.json();
        setCurrentPoll(data);
        setPollQ(data.question || '');
        const opts = data.options || [];
        setPollOpts([
          opts[0]?.text || '',
          opts[1]?.text || '',
          opts[2]?.text || '',
          opts[3]?.text || '',
        ]);
      } else {
        setCurrentPoll(null);
      }
    } catch { /* ignore */ }
    setPollLoading(false);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoginErr('');
    const form = e.target;
    const base = (API || '').replace(/\/$/, '');
    try {
      const res = await fetch(`${base}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username.value, password: form.password.value }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        return setLoginErr(
          res.status >= 500
            ? `API returned ${res.status} (not JSON). Check CDN/proxy and /api/ping.`
            : 'Server sent a non-JSON response. Open /api/ping in the browser.',
        );
      }
      if (!res.ok) return setLoginErr(data.error || `Login failed (${res.status})`);
      if (!data.token) return setLoginErr('No token from server. Check API URL and /api/health.');
      localStorage.setItem('rk_admin_token', data.token);
      setToken(data.token);
      setScreen('dashboard');
    } catch {
      setLoginErr('Network error — cannot reach API. Ensure the server is running and /api/health works.');
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return showToast('Please select a PDF file', 'error');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('paper', file);
      fd.append('date', date);
      const res = await fetch(`${API}/api/admin/upload`, {
        method: 'POST',
        headers: adminHeaders(token),
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (!res.ok) showToast(adminAuthErrorMessage(res, data), 'error');
      else {
        setWhatsappConfigured(!!data.whatsappConfigured);
        setApnsConfigured(!!data.apnsConfigured);
        setLastUploadMeta({
          date: data.date || date,
          scheduled: !!data.scheduled,
        });
        if (data.scheduled) {
          showToast(
            `Scheduled for ${formatDateEn(data.date || date)} — readers see it on that date. WhatsApp goes out after midnight IST.`
          );
        } else {
          const shareText = `📰 Repatikosam — ${formatDateEn(date)} edition is live!\nRead now: https://repatikosam.com`;
          try {
            await navigator.clipboard.writeText(shareText);
            showToast('Published live! Share text copied. You can notify paid subscribers on WhatsApp below.');
          } catch {
            showToast(`Daily edition live for ${formatDateEn(date)}`);
          }
          if (data.notify?.configured) {
            showToast(
              `Auto WhatsApp: sent ${data.notify.sent || 0}, failed ${data.notify.failed || 0}`,
              data.notify.failed ? 'error' : undefined
            );
          }
        }
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
        fetchPapers();
      }
    } catch {
      showToast('Server error', 'error');
    }
    setUploading(false);
  }

  async function handleDelete(d) {
    if (!window.confirm(`Delete daily edition for ${formatDateEn(d)}?`)) return;
    setDeleting(d);
    const res = await fetch(`${API}/api/admin/papers/${d}`, {
      method: 'DELETE',
      headers: adminHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    setDeleting(null);
    if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
    if (res.ok) { showToast('Edition deleted'); fetchPapers(); }
    else showToast(adminAuthErrorMessage(res, data), 'error');
  }

  async function handleMonthlyUpload(e) {
    e.preventDefault();
    if (!monthlyFile) return showToast('Please select a PDF file', 'error');
    setMonthlyUploading(true);
    try {
      const fd = new FormData();
      fd.append('paper', monthlyFile);
      fd.append('month', monthKey);
      const res = await fetch(`${API}/api/admin/monthly-upload`, {
        method: 'POST',
        headers: adminHeaders(token),
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (!res.ok) showToast(adminAuthErrorMessage(res, data), 'error');
      else {
        showToast(`Monthly magazine uploaded for ${formatMonthEn(monthKey)}`);
        setMonthlyFile(null);
        if (monthlyFileRef.current) monthlyFileRef.current.value = '';
        fetchMonthlyPapers();
      }
    } catch {
      showToast('Server error', 'error');
    }
    setMonthlyUploading(false);
  }

  async function handleMonthlyDelete(m) {
    if (!window.confirm(`Delete monthly magazine for ${formatMonthEn(m)}?`)) return;
    setMonthlyDeleting(m);
    const res = await fetch(`${API}/api/admin/monthly-papers/${m}`, {
      method: 'DELETE',
      headers: adminHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    setMonthlyDeleting(null);
    if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
    if (res.ok) { showToast('Monthly magazine deleted'); fetchMonthlyPapers(); }
    else showToast(adminAuthErrorMessage(res, data), 'error');
  }

  function handleImagePick(f) {
    if (!f) return;
    if (f.size > MAX_STORY_IMAGE_BYTES) {
      showToast(`Image too large — max ${MAX_STORY_IMAGE_BYTES / (1024 * 1024)}MB`, 'error');
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  }

  async function handleSaveStory(e) {
    e.preventDefault();
    if (!storyForm.title.trim()) return showToast('Telugu headline is required', 'error');
    if (!editingStoryId && !imageFile) return showToast('Please upload a cover image', 'error');
    if (!storyForm.body.trim() && !storyForm.excerpt.trim()) {
      return showToast('Add article text or a card summary', 'error');
    }

    setSavingStory(true);
    try {
      const fd = new FormData();
      fd.append('title', storyForm.title.trim());
      fd.append('titleEn', storyForm.titleEn.trim() || storyForm.title.trim());
      fd.append('excerpt', storyForm.excerpt || '');
      fd.append('excerptEn', storyForm.excerptEn || '');
      fd.append('body', storyForm.body || '');
      fd.append('bodyEn', storyForm.bodyEn || '');
      const catId = storyForm.categoryId || resolveStoryCategoryId(storyForm) || 'politics';
      const catFields = storyCategoryFields(catId);
      fd.append('category', catFields.category);
      fd.append('categoryEn', catFields.categoryEn);
      fd.append('color', storyForm.color || '#1a6b2f');
      fd.append('featured', storyForm.featured ? '1' : '0');
      fd.append('readerLayout', storyForm.readerLayout || 'landscape');
      fd.append('cardAspect', storyForm.cardAspect || 'landscape');
      fd.append('publishAt', storyForm.publishAt || todayIST());
      if (imageFile) {
        fd.append('image', imageFile, imageFile.name || 'story.jpg');
      }

      const url = editingStoryId
        ? `${API}/api/admin/stories/${editingStoryId}`
        : `${API}/api/admin/stories`;
      const res = await fetch(url, {
        method: editingStoryId ? 'PUT' : 'POST',
        headers: adminHeaders(token),
        body: fd,
      });
      const data = await parseApiJson(res);
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (res.ok && data.success) {
        if (data.scheduled) {
          showToast(`Scheduled for ${formatDateEn(data.publishAt || storyForm.publishAt)} — readers see it on that date`);
        } else {
          showToast(editingStoryId ? 'Story updated' : 'Story published');
        }
        resetStoryForm();
        fetchStories();
      } else {
        showToast(data.error || `Save failed (${res.status})`, 'error');
      }
    } catch {
      showToast('Network error', 'error');
    }
    setSavingStory(false);
  }

  async function handleDeleteStory(id) {
    if (!window.confirm('Delete this story?')) return;
    setDeletingStory(id);
    const delRes = await fetch(`${API}/api/admin/stories/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(token),
    });
    if (delRes.status === 401) {
      const d = await delRes.json().catch(() => ({}));
      setDeletingStory(null);
      return logoutAdmin(adminAuthErrorMessage(delRes, d));
    }
    setDeletingStory(null);
    if (editingStoryId === id) resetStoryForm();
    fetchStories();
    showToast('Story removed');
  }

  async function handleToggleFeatured(id) {
    const res = await fetch(`${API}/api/admin/stories/${id}/featured`, {
      method: 'PUT',
      headers: adminHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
    if (res.ok) {
      showToast(data.featured ? "Added to Editor's pick" : "Removed from Editor's pick");
      fetchStories();
    } else {
      showToast(data.error || 'Could not update featured', 'error');
    }
  }

  async function handleSavePoll(e) {
    e.preventDefault();
    const opts = pollOpts.filter((o) => o.trim());
    if (!pollQ.trim()) return showToast('Enter a question', 'error');
    if (opts.length < 2) return showToast('At least 2 options required', 'error');
    setSavingPoll(true);
    try {
      const res = await fetch(`${API}/api/admin/poll`, {
        method: 'PUT',
        headers: adminHeaders(token, { json: true }),
        body: JSON.stringify({ question: pollQ, options: opts }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (res.ok) {
        showToast('Poll saved — vote counts reset');
        fetchPoll();
      } else showToast(adminAuthErrorMessage(res, data), 'error');
    } catch {
      showToast('Server error', 'error');
    }
    setSavingPoll(false);
  }

  async function handleAddHeadline(e) {
    e.preventDefault();
    if (!newHeadline.trim()) return showToast('Enter a headline', 'error');
    setSavingHeadline(true);
    try {
      const res = await fetch(`${API}/api/admin/breaking-news`, {
        method: 'POST',
        headers: adminHeaders(token, { json: true }),
        body: JSON.stringify({ headline: newHeadline }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) return logoutAdmin(adminAuthErrorMessage(res, data));
      if (res.ok) { showToast('Headline added'); setNewHeadline(''); fetchBreakingNews(); }
      else showToast(adminAuthErrorMessage(res, data), 'error');
    } catch {
      showToast('Server error', 'error');
    }
    setSavingHeadline(false);
  }

  async function handleDeleteHeadline(id) {
    if (!window.confirm('Delete this headline?')) return;
    setDeletingHeadline(id);
    const delRes = await fetch(`${API}/api/admin/breaking-news/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(token),
    });
    if (delRes.status === 401) {
      const d = await delRes.json().catch(() => ({}));
      setDeletingHeadline(null);
      return logoutAdmin(adminAuthErrorMessage(delRes, d));
    }
    setDeletingHeadline(null);
    fetchBreakingNews();
    showToast('Headline removed');
  }

  const sidebarAdCount = ads.filter((a) => a.placement === 'sidebar').length;
  const bannerAdCount = ads.filter((a) => a.placement === 'banner').length;
  const activeAdCount = ads.filter((a) => a.active !== false).length;

  const badge = (id) => {
    if (id === 'papers') return papers.length;
    if (id === 'monthly') return monthlyPapers.length;
    if (id === 'stories') return stories.length;
    if (id === 'ads') return ads.length;
    if (id === 'breaking') return breakingNews.length;
    return 0;
  };

  const quickMeta = (id) => {
    if (id === 'papers') return `${papers.length} edition${papers.length === 1 ? '' : 's'}`;
    if (id === 'monthly') return `${monthlyPapers.length} edition${monthlyPapers.length === 1 ? '' : 's'}`;
    if (id === 'stories') return `${stories.filter((s) => !s.scheduled).length} live · ${stories.filter((s) => s.scheduled).length} scheduled`;
    if (id === 'ads') {
      if (!ads.length) return 'None yet — add sidebar or banner ads';
      return `${ads.length} total · ${sidebarAdCount} sidebar · ${bannerAdCount} banner · ${activeAdCount} live`;
    }
    if (id === 'breaking') return `${breakingNews.length} active headline${breakingNews.length === 1 ? '' : 's'}`;
    if (id === 'poll') return 'Update site poll';
    if (id === 'subscribers') return `${registeredUsers.length} Google sign-ins`;
    if (id === 'payments') return 'Razorpay order history';
    if (id === 'site') return 'LIVE badge, banner, WhatsApp help';
    return '';
  };

  if (screen === 'login') {
    return (
      <div className="adm-root adm-login-screen">
        <div className="adm-login-panel">
          <div className="adm-login-brand">
            <img src="/rk-logo.webp" alt="Repatikosam" onError={(e) => { e.target.style.display = 'none'; }} />
            <h1>Repatikosam</h1>
            <p>Content Management</p>
          </div>
          <form onSubmit={handleLogin} className="adm-login-form">
            <div className="adm-field">
              <label>Username</label>
              <input name="username" type="text" placeholder="admin" required autoComplete="username" />
            </div>
            <div className="adm-field">
              <label>Password</label>
              <input name="password" type="password" placeholder="••••••••" required autoComplete="current-password" />
            </div>
            {loginErr && <div className="adm-error">{loginErr}</div>}
            <button type="submit" className="adm-btn adm-btn-primary" style={{ width: '100%' }}>
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  const section = SECTION_COPY[tab] || SECTION_COPY.overview;

  return (
    <div className="adm-root">
      {toast && <div className={`adm-toast ${toast.type}`}>{toast.msg}</div>}

      <div className="adm-shell">
        {navOpen && (
          <button
            type="button"
            className="adm-nav-overlay"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          />
        )}

        <aside className={`adm-sidebar ${navOpen ? 'adm-sidebar--open' : ''}`}>
          <div className="adm-sidebar-brand">
            <img src="/rk-logo.webp" alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <strong>Repatikosam</strong>
              <span>Admin</span>
            </div>
            <button
              type="button"
              className="adm-nav-close adm-mobile-only"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
            >
              ×
            </button>
          </div>
          <nav className="adm-nav" aria-label="Admin sections">
            {NAV_GROUPS.map((g) => (
              <div key={g.id} className="adm-nav-group">
                <span className="adm-nav-group-label">{g.label}</span>
                {NAV.filter((n) => n.group === g.id).map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`adm-nav-btn ${tab === n.id ? 'active' : ''}`}
                    onClick={() => goToTab(n.id)}
                  >
                    <span className="adm-nav-icon">{n.icon}</span>
                    {n.label}
                    {badge(n.id) > 0 && n.id !== 'overview' && n.id !== 'poll' && n.id !== 'site' && (
                      <span className="adm-nav-badge">{badge(n.id)}</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className="adm-sidebar-foot">
            <button
              type="button"
              className="adm-logout-btn"
              onClick={() => { localStorage.removeItem('rk_admin_token'); setToken(''); setScreen('login'); }}
            >
              Sign out
            </button>
          </div>
        </aside>

        <div className="adm-content">
          <header className="adm-mobile-header adm-mobile-only">
            <button
              type="button"
              className="adm-menu-btn"
              aria-label="Open menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
            >
              <span className="adm-menu-icon" aria-hidden />
            </button>
            <div className="adm-mobile-header-title">
              <strong>{section.title}</strong>
              <span>{NAV.find((n) => n.id === tab)?.label}</span>
            </div>
            <button
              type="button"
              className="adm-mobile-signout"
              onClick={() => { localStorage.removeItem('rk_admin_token'); setToken(''); setScreen('login'); }}
            >
              Out
            </button>
          </header>

          <main className="adm-main">
            <header className="adm-topbar">
              <div className="adm-topbar-row">
                <div>
                  <h2>{section.title}</h2>
                  <p>{section.desc}</p>
                </div>
              </div>
            </header>

          {tab === 'overview' && (
            <>
              <div className="adm-overview-actions">
                <a href="/" target="_blank" rel="noreferrer" className="adm-btn adm-btn-ghost">
                  View live site ↗
                </a>
                <a href="/login" target="_blank" rel="noreferrer" className="adm-btn adm-btn-ghost">
                  Reader login page ↗
                </a>
              </div>
              <div className="adm-stats">
                <div className="adm-stat">
                  <div className="adm-stat-value">{papers.length}</div>
                  <div className="adm-stat-label">Daily editions</div>
                </div>
                <div className="adm-stat">
                  <div className="adm-stat-value">{monthlyPapers.length}</div>
                  <div className="adm-stat-label">Monthly magazines</div>
                </div>
                <div className="adm-stat">
                  <div className="adm-stat-value">{stories.length}</div>
                  <div className="adm-stat-label">Top stories</div>
                </div>
                <div className="adm-stat adm-stat--highlight">
                  <div className="adm-stat-value">{ads.length}</div>
                  <div className="adm-stat-label">Advertisements</div>
                  <div className="adm-stat-sub">
                    {ads.length === 0
                      ? 'Add sidebar (max 2 on site) or top partner banners'
                      : `${sidebarAdCount} sidebar · ${bannerAdCount} banner · ${activeAdCount} live on site`}
                  </div>
                </div>
                <div className="adm-stat">
                  <div className="adm-stat-value">{breakingNews.length}</div>
                  <div className="adm-stat-label">Breaking headlines</div>
                </div>
                <div className="adm-stat">
                  <div className="adm-stat-value">{registeredUsers.length}</div>
                  <div className="adm-stat-label">Google sign-ins</div>
                  <div className="adm-stat-sub">Readers who logged in with Gmail</div>
                </div>
              </div>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div><h3>Quick actions</h3><p>Jump to any section — counts show what is live on the site.</p></div>
                </div>
                <div className="adm-quick-links">
                  {NAV.filter((n) => n.id !== 'overview').map((n) => (
                    <button key={n.id} type="button" className="adm-quick-card" onClick={() => goToTab(n.id)}>
                      <div className="adm-quick-card-top">
                        <strong>{n.icon} {n.label}</strong>
                        {badge(n.id) > 0 && n.id !== 'poll' && (
                          <span className="adm-quick-badge">{badge(n.id)}</span>
                        )}
                      </div>
                      <span className="adm-quick-meta">{quickMeta(n.id)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {tab === 'subscribers' && (
            <AdminSubscribers token={token} onLogout={logoutAdmin} showToast={showToast} />
          )}

          {tab === 'payments' && (
            <AdminPayments token={token} onLogout={logoutAdmin} showToast={showToast} />
          )}

          {tab === 'site' && (
            <AdminSiteSettings token={token} onLogout={logoutAdmin} showToast={showToast} />
          )}

          {tab === 'papers' && (
            <>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div>
                    <h3>Upload daily PDF</h3>
                    <p>Step 1: Pick the edition date · Step 2: Select PDF · Step 3: Upload</p>
                  </div>
                  <span className="adm-pill">Max 50 MB</span>
                </div>
                <div className="adm-steps">
                  <span className={`adm-step ${date ? 'active' : ''}`}><span className="adm-step-num">1</span> Pick date</span>
                  <span className={`adm-step ${file ? 'active' : ''}`}><span className="adm-step-num">2</span> Select PDF</span>
                  <span className={`adm-step ${file && date ? 'active' : ''}`}><span className="adm-step-num">3</span> Publish</span>
                </div>
                <form onSubmit={handleUpload} className="adm-form">
                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Edition date (IST)</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        max={maxScheduleIST()}
                        required
                      />
                      {date && (
                        <span className="adm-upload-ready">
                          Date: {formatDateEn(date)}
                          {isFutureEdition(date) ? ' · Scheduled (hidden until that date)' : ' · Live for readers'}
                        </span>
                      )}
                      <p className="adm-field-hint">
                        Schedule up to {SCHEDULE_HORIZON_DAYS} days ahead (max {maxScheduleIST()}).
                        Readers only see the edition on that date. Paid WhatsApp alerts go out when the edition becomes today.
                      </p>
                    </div>
                  </div>
                  <UploadDrop label="PDF file" hint="PDF only · max 50 MB · replaces existing file for this date" accept="application/pdf" file={file} onPick={setFile} inputRef={fileRef} />
                  <button type="submit" className="adm-btn adm-btn-primary" disabled={uploading || !file}>
                    {uploading
                      ? <><span className="adm-spinner" /> Uploading…</>
                      : isFutureEdition(date)
                        ? 'Schedule daily edition'
                        : 'Publish daily edition'}
                  </button>
                </form>
                {lastUploadMeta && !lastUploadMeta.scheduled && (
                  <div className="adm-notify-bar">
                    <div>
                      <strong>Paper is live</strong>
                      <p>
                        {whatsappConfigured || apnsConfigured
                          ? 'Notify readers on WhatsApp and/or iPhone push (devices that opted in).'
                          : 'Configure WhatsApp and/or APNs on the server to notify readers.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="adm-btn adm-btn-primary"
                      disabled={(!whatsappConfigured && !apnsConfigured) || notifyingDate === lastUploadMeta.date}
                      onClick={() => handleNotifyWhatsApp(lastUploadMeta.date)}
                    >
                      {notifyingDate === lastUploadMeta.date
                        ? <><span className="adm-spinner" /> Sending…</>
                        : 'Notify readers'}
                    </button>
                  </div>
                )}
              </div>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div>
                    <h3>Published editions</h3>
                    <p>Daily PDFs — scheduled (tomorrow) stay hidden from readers until that date.</p>
                  </div>
                  <span className="adm-pill">{papers.length} total</span>
                </div>
                {papers.length === 0 ? (
                  <div className="adm-empty"><div className="adm-empty-icon">📰</div>No daily editions yet.</div>
                ) : (
                  <div className="adm-list">
                    {papers.map((dk, i) => (
                      <div key={dk} className="adm-list-item">
                        <div className="adm-list-icon">{papers.length - i}</div>
                        <div className="adm-list-body">
                          <div className="adm-list-title">
                            {formatDateEn(dk)}{' '}
                            {isFutureEdition(dk) && (
                              <span className="adm-status adm-status--trial">Scheduled</span>
                            )}
                            {!isFutureEdition(dk) && dk === todayIST() && (
                              <span className="adm-status adm-status--paid">Live</span>
                            )}
                          </div>
                          <div className="adm-list-sub">{dk}.pdf</div>
                        </div>
                        <div className="adm-list-actions">
                          {!isFutureEdition(dk) && (
                            <button
                              type="button"
                              className="adm-btn adm-btn-ghost"
                              disabled={(!whatsappConfigured && !apnsConfigured) || notifyingDate === dk}
                              onClick={() => handleNotifyWhatsApp(dk)}
                              title="WhatsApp + iPhone push"
                            >
                              {notifyingDate === dk ? '…' : 'Notify'}
                            </button>
                          )}
                          <button type="button" className="adm-btn adm-btn-danger" onClick={() => handleDelete(dk)} disabled={deleting === dk}>
                            {deleting === dk ? <span className="adm-spinner dark" /> : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'monthly' && (
            <>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div><h3>Upload monthly PDF</h3><p>One PDF per month — stored separately from daily editions.</p></div>
                  <span className="adm-pill">Max 50 MB</span>
                </div>
                <div className="adm-steps">
                  <span className={`adm-step ${monthKey ? 'active' : ''}`}><span className="adm-step-num">1</span> Pick month</span>
                  <span className={`adm-step ${monthlyFile ? 'active' : ''}`}><span className="adm-step-num">2</span> Select PDF</span>
                  <span className={`adm-step ${monthlyFile && monthKey ? 'active' : ''}`}><span className="adm-step-num">3</span> Publish</span>
                </div>
                <form onSubmit={handleMonthlyUpload} className="adm-form">
                  <div className="adm-field">
                    <label>Month</label>
                    <input type="month" value={monthKey} onChange={(e) => setMonthKey(e.target.value)} max={new Date().toISOString().slice(0, 7)} required />
                    {monthKey && <span className="adm-upload-ready">Month: {formatMonthEn(monthKey)}</span>}
                  </div>
                  <UploadDrop label="PDF file" hint="PDF only · max 50 MB · monthly magazine" accept="application/pdf" file={monthlyFile} onPick={setMonthlyFile} inputRef={monthlyFileRef} icon="📖" />
                  <button type="submit" className="adm-btn adm-btn-primary" disabled={monthlyUploading || !monthlyFile}>
                    {monthlyUploading ? <><span className="adm-spinner" /> Uploading…</> : 'Publish monthly magazine'}
                  </button>
                </form>
              </div>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div><h3>Published monthly magazines</h3></div>
                  <span className="adm-pill">{monthlyPapers.length} total</span>
                </div>
                {monthlyPapers.length === 0 ? (
                  <div className="adm-empty"><div className="adm-empty-icon">📖</div>No monthly magazines yet.</div>
                ) : (
                  <div className="adm-list">
                    {monthlyPapers.map((mk, i) => (
                      <div key={mk} className="adm-list-item">
                        <div className="adm-list-icon">M</div>
                        <div className="adm-list-body">
                          <div className="adm-list-title">{formatMonthEn(mk)}</div>
                          <div className="adm-list-sub">{mk}.pdf</div>
                        </div>
                        <button type="button" className="adm-btn adm-btn-danger" onClick={() => handleMonthlyDelete(mk)} disabled={monthlyDeleting === mk}>
                          {monthlyDeleting === mk ? <span className="adm-spinner dark" /> : 'Delete'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'stories' && (
            <>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div>
                    <h3>{editingStoryId ? 'Edit exclusive story' : 'Add exclusive story'}</h3>
                    <p>
                      Pick today’s date to go live now, or any date within the next {SCHEDULE_HORIZON_DAYS} days to schedule.
                      Readers only see it on that date.
                    </p>
                  </div>
                  {editingStoryId && (
                    <button type="button" className="adm-btn adm-btn-ghost" onClick={resetStoryForm}>Cancel edit</button>
                  )}
                </div>
                <form onSubmit={handleSaveStory} className="adm-form">
                  <div className="adm-row">
                    <div className="adm-field">
                      <label>
                        Publish date (IST)
                        {isFutureEdition(storyForm.publishAt)
                          ? ' · Scheduled (hidden until that date)'
                          : ' · Live for readers'}
                      </label>
                      <input
                        type="date"
                        value={storyForm.publishAt || todayIST()}
                        max={maxScheduleIST()}
                        onChange={(e) => setStoryForm((f) => ({ ...f, publishAt: e.target.value }))}
                        required
                      />
                      <p className="adm-field-hint">
                        Schedule up to {SCHEDULE_HORIZON_DAYS} days ahead (max {maxScheduleIST()}).
                        Readers only see the story on that date.
                      </p>
                    </div>
                  </div>
                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Headline (Telugu) *</label>
                      <input type="text" value={storyForm.title} onChange={(e) => setStoryForm((f) => ({ ...f, title: e.target.value }))} placeholder="Story headline" required />
                    </div>
                    <div className="adm-field">
                      <label>Headline (English)</label>
                      <input type="text" value={storyForm.titleEn} onChange={(e) => setStoryForm((f) => ({ ...f, titleEn: e.target.value }))} placeholder="English headline" />
                    </div>
                  </div>

                  <div className="adm-field">
                    <div className="adm-field-label-row">
                      <label>Full article (Telugu)</label>
                      <button type="button" className="adm-btn-link" onClick={() => fillCardSummaryFromBody('te')}>
                        Use first paragraph as card summary
                      </button>
                    </div>
                    <textarea
                      className="adm-textarea-tall"
                      value={storyForm.body}
                      onChange={(e) => setStoryForm((f) => ({ ...f, body: e.target.value }))}
                      placeholder="Paste the full article. Blank line = new paragraph."
                      rows={10}
                    />
                    {storyForm.body.trim() && (
                      <div className="adm-excerpt-preview">
                        <span className="adm-excerpt-preview-label">Article preview</span>
                        <StoryExcerpt text={storyForm.body} className="adm-excerpt-preview-body" paragraphClassName="adm-excerpt-preview-p" />
                      </div>
                    )}
                  </div>

                  <div className="adm-field">
                    <div className="adm-field-label-row">
                      <label>Full article (English)</label>
                      <button type="button" className="adm-btn-link" onClick={() => fillCardSummaryFromBody('en')}>
                        Use first paragraph as card summary
                      </button>
                    </div>
                    <textarea
                      className="adm-textarea-tall"
                      value={storyForm.bodyEn}
                      onChange={(e) => setStoryForm((f) => ({ ...f, bodyEn: e.target.value }))}
                      placeholder="English version of the article (optional)."
                      rows={8}
                    />
                  </div>

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Card summary (Telugu)</label>
                      <textarea
                        value={storyForm.excerpt}
                        onChange={(e) => setStoryForm((f) => ({ ...f, excerpt: e.target.value }))}
                        placeholder="Short text on the homepage card (1–3 lines)."
                        rows={3}
                      />
                    </div>
                    <div className="adm-field">
                      <label>Card summary (English)</label>
                      <textarea
                        value={storyForm.excerptEn}
                        onChange={(e) => setStoryForm((f) => ({ ...f, excerptEn: e.target.value }))}
                        placeholder="English card text."
                        rows={3}
                      />
                    </div>
                  </div>
                  <p className="adm-field-hint">
                    Leave card summary empty when publishing and we auto-fill from the first paragraph of the article.
                  </p>

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Recommended upload shape</label>
                      <select
                        value={storyForm.cardAspect}
                        onChange={(e) => setStoryForm((f) => ({ ...f, cardAspect: e.target.value }))}
                      >
                        <option value="landscape">Landscape — 1200×675 px (16:9)</option>
                        <option value="portrait">Portrait — 900×1200 px (3:4)</option>
                        <option value="square">Square — 1000×1000 px (1:1)</option>
                      </select>
                      <p className="adm-field-hint">
                        {STORY_CARD_ASPECT_HINTS[storyForm.cardAspect]} · ★ Editor&apos;s pick shows in the top carousel; other stories show as squares below.
                      </p>
                    </div>
                    <div className="adm-field">
                      <label>Article reader layout (when opened)</label>
                      <select
                        value={storyForm.readerLayout}
                        onChange={(e) => setStoryForm((f) => ({ ...f, readerLayout: e.target.value }))}
                      >
                        <option value="landscape">Landscape — image on top, text below</option>
                        <option value="portrait">Portrait — image beside text (tablet/desktop)</option>
                      </select>
                      <p className="adm-field-hint">
                        Portrait shows image left and article right on larger screens; mobile always stacks for easy reading.
                      </p>
                    </div>
                  </div>

                  <UploadDrop
                    label={editingStoryId ? 'Replace cover image (optional)' : 'Cover image *'}
                    hint={`JPG, PNG, or WebP · max 8 MB · ${STORY_CARD_ASPECT_HINTS[storyForm.cardAspect]}`}
                    accept="image/*"
                    file={imageFile}
                    onPick={handleImagePick}
                    inputRef={imageRef}
                    icon="🖼️"
                  />
                  {imagePreview && (
                    <div className="adm-img-preview-wrap">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="adm-btn adm-btn-ghost"
                        style={{ marginTop: 8 }}
                        onClick={() => { setImageFile(null); if (!editingStoryId) setImagePreview(''); }}
                      >
                        {editingStoryId ? 'Clear new image' : 'Remove image'}
                      </button>
                    </div>
                  )}

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Category</label>
                      <select
                        value={storyForm.categoryId || resolveStoryCategoryId(storyForm) || 'politics'}
                        onChange={(e) => {
                          const id = e.target.value;
                          const fields = storyCategoryFields(id);
                          setStoryForm((f) => ({
                            ...f,
                            categoryId: id,
                            category: fields.category,
                            categoryEn: fields.categoryEn,
                          }));
                        }}
                      >
                        {STORY_CATEGORIES.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.en} — {c.te}
                          </option>
                        ))}
                      </select>
                      <p className="adm-field-hint">
                        Homepage chips: All · Politics · News · Legacy Of TDP (పసుపు పరిమళాలు)
                      </p>
                    </div>
                    <div className="adm-field adm-field-color">
                      <label>Tag color</label>
                      <input type="color" value={storyForm.color} onChange={(e) => setStoryForm((f) => ({ ...f, color: e.target.value }))} />
                    </div>
                  </div>
                  <label className="adm-checkbox">
                    <input type="checkbox" checked={storyForm.featured} onChange={(e) => setStoryForm((f) => ({ ...f, featured: e.target.checked }))} />
                    ★ Editor&apos;s pick — appears in the auto-scrolling carousel at the top (star multiple stories)
                  </label>
                  <div className="adm-form-actions">
                    <button type="submit" className="adm-btn adm-btn-primary" disabled={savingStory}>
                      {savingStory
                        ? <><span className="adm-spinner" /> Saving…</>
                        : editingStoryId
                          ? 'Save changes'
                          : isFutureEdition(storyForm.publishAt)
                            ? 'Schedule story'
                            : 'Publish story'}
                    </button>
                    {editingStoryId && (
                      <button type="button" className="adm-btn adm-btn-ghost" onClick={resetStoryForm}>Cancel</button>
                    )}
                  </div>
                </form>
              </div>
              {orderedStories.length > 0 && (
                <div className="adm-card">
                  <div className="adm-card-head">
                    <div>
                      <h3>Stories</h3>
                      <p className="adm-card-sub">Drag ⋮⋮ to change homepage order. Scheduled stories stay hidden from readers until their date.</p>
                    </div>
                    <span className="adm-pill">
                      {savingOrder
                        ? 'Saving order…'
                        : `${orderedStories.filter((s) => !s.scheduled).length} live · ${orderedStories.filter((s) => s.scheduled).length} scheduled`}
                    </span>
                  </div>
                  <div className="adm-list adm-list-sortable">
                    {orderedStories.map((s) => (
                      <div
                        key={s.id}
                        className={`adm-list-item adm-list-item-draggable ${dragStoryId.current === s.id ? 'is-dragging' : ''}`}
                        draggable
                        onDragStart={() => { dragStoryId.current = s.id; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromId = dragStoryId.current;
                          dragStoryId.current = null;
                          const next = reorderStoriesList(fromId, s.id);
                          if (next) persistStoryOrder(next);
                        }}
                        onDragEnd={() => { dragStoryId.current = null; }}
                      >
                        <span className="adm-drag-handle" title="Drag to reorder" aria-hidden>⋮⋮</span>
                        <img src={s.image?.startsWith('http') ? s.image : `${API}${s.image}`} alt="" className="adm-story-thumb" />
                        <div className="adm-list-body">
                          <div className="adm-list-title">{s.title}</div>
                          <div className="adm-list-sub">
                            <span className="adm-tag" style={{ background: s.color }}>
                              {storyCategoryLabel(resolveStoryCategoryId(s) || 'news', 'en')}
                            </span>
                            {s.scheduled && (
                              <span className="adm-status adm-status--trial">Scheduled {s.publishAt}</span>
                            )}
                            {!s.scheduled && s.publishAt === todayIST() && (
                              <span className="adm-status adm-status--paid">Live</span>
                            )}
                            {s.titleEn && <span className="adm-list-meta">EN: {s.titleEn}</span>}
                            {s.featured && <span className="adm-feat">★ Editor&apos;s pick</span>}
                          </div>
                        </div>
                        <div className="adm-list-actions">
                          <button type="button" className="adm-btn adm-btn-ghost" onClick={() => startEditStory(s)} title="Edit story">
                            Edit
                          </button>
                          <button
                            type="button"
                            className={`adm-btn adm-btn-featured ${s.featured ? 'is-active' : ''}`}
                            onClick={() => handleToggleFeatured(s.id)}
                            title={s.featured ? "Remove from Editor's pick" : "Add to Editor's pick carousel"}
                          >
                            ★
                          </button>
                          <button type="button" className="adm-btn adm-btn-danger" onClick={() => handleDeleteStory(s.id)} disabled={deletingStory === s.id}>
                            {deletingStory === s.id ? <span className="adm-spinner dark" /> : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'ads' && (
            <>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div>
                    <h3>{editingAdId ? 'Edit advertisement' : 'Create advertisement'}</h3>
                    <p>
                      <strong>Sidebar</strong> — up to 2 show in the right column.{' '}
                      <strong>Banner</strong> — wide strip above the footer.
                    </p>
                  </div>
                  {editingAdId && (
                    <button type="button" className="adm-btn adm-btn-ghost" onClick={resetAdForm}>Cancel edit</button>
                  )}
                </div>
                <form onSubmit={handleSaveAd} className="adm-form">
                  <div className="adm-field">
                    <label>Placement *</label>
                    <div className="adm-segment">
                      <button
                        type="button"
                        className={`adm-segment-btn ${adForm.placement === 'sidebar' ? 'active' : ''}`}
                        onClick={() => setAdForm((f) => ({ ...f, placement: 'sidebar' }))}
                      >
                        Sidebar (×2 slots)
                      </button>
                      <button
                        type="button"
                        className={`adm-segment-btn ${adForm.placement === 'banner' ? 'active' : ''}`}
                        onClick={() => setAdForm((f) => ({ ...f, placement: 'banner' }))}
                      >
                        Top partner banner
                      </button>
                    </div>
                    {adForm.mediaType === 'image' && (
                      <div className="adm-ad-spec" role="note">
                        <div className="adm-ad-spec__preview" aria-hidden>
                          <span
                            className={`adm-ad-spec__frame adm-ad-spec__frame--${adForm.placement}`}
                          />
                        </div>
                        <div className="adm-ad-spec__copy">
                          <strong>{getAdImageSpec(adForm.placement).label} — perfect image size</strong>
                          <p className="adm-ad-spec__size">
                            Upload at <strong>{getAdImageSpec(adForm.placement).width} × {getAdImageSpec(adForm.placement).height} px</strong>
                            {' '}({getAdImageSpec(adForm.placement).ratio})
                          </p>
                          <p className="adm-ad-spec__note">{getAdImageSpec(adForm.placement).displayNote}</p>
                          <p className="adm-ad-spec__tip">{getAdImageSpec(adForm.placement).tip}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="adm-field">
                    <label>Media type *</label>
                    <div className="adm-segment">
                      <button
                        type="button"
                        className={`adm-segment-btn ${adForm.mediaType === 'image' ? 'active' : ''}`}
                        onClick={() => setAdForm((f) => ({ ...f, mediaType: 'image' }))}
                      >
                        Image
                      </button>
                      <button
                        type="button"
                        className={`adm-segment-btn ${adForm.mediaType === 'video' ? 'active' : ''}`}
                        onClick={() => setAdForm((f) => ({ ...f, mediaType: 'video' }))}
                      >
                        Video
                      </button>
                    </div>
                  </div>

                  {adForm.mediaType === 'image' ? (
                    <UploadDrop
                      label={editingAdId ? 'Replace image (optional)' : 'Ad image *'}
                      hint={adImageUploadHint(adForm.placement)}
                      accept="image/*"
                      file={adImageFile}
                      onPick={(f) => {
                        if (!f) return;
                        if (f.size > MAX_STORY_IMAGE_BYTES) {
                          showToast('Image too large (max 8MB)', 'error');
                          return;
                        }
                        setAdImageFile(f);
                        setAdImagePreview(URL.createObjectURL(f));
                      }}
                      inputRef={adImageRef}
                      icon="🖼️"
                    />
                  ) : (
                    <>
                      <UploadDrop
                        label={editingAdId ? 'Replace video file (optional)' : 'Upload MP4 video'}
                        hint="MP4 or WebM · max 50 MB · or use a URL below"
                        accept="video/mp4,video/webm,.mp4,.webm"
                        file={adVideoFile}
                        onPick={(f) => {
                          if (!f) return;
                          if (f.size > MAX_AD_VIDEO_BYTES) {
                            showToast('Video too large (max 50MB)', 'error');
                            return;
                          }
                          setAdVideoFile(f);
                          setAdVideoPreview(URL.createObjectURL(f));
                          setAdForm((prev) => ({ ...prev, videoUrl: '' }));
                        }}
                        inputRef={adVideoRef}
                        icon="🎬"
                      />
                      {adVideoPreview && (
                        <div className="adm-img-preview-wrap adm-video-preview-wrap">
                          <video src={adVideoPreview} controls playsInline muted preload="metadata" />
                          <button
                            type="button"
                            className="adm-btn adm-btn-ghost"
                            style={{ marginTop: 8 }}
                            onClick={() => {
                              setAdVideoFile(null);
                              setAdVideoPreview('');
                              if (adVideoRef.current) adVideoRef.current.value = '';
                            }}
                          >
                            Remove video file
                          </button>
                        </div>
                      )}
                      <div className="adm-field">
                        <label>Or video URL</label>
                        <input
                          type="url"
                          value={adForm.videoUrl}
                          onChange={(e) => {
                            setAdForm((f) => ({ ...f, videoUrl: e.target.value }));
                            if (e.target.value.trim()) {
                              setAdVideoFile(null);
                              setAdVideoPreview('');
                              if (adVideoRef.current) adVideoRef.current.value = '';
                            }
                          }}
                          placeholder="YouTube, Facebook embed, or direct .mp4 link"
                          disabled={!!adVideoFile}
                        />
                        <p className="adm-field-hint">
                          Upload an MP4 for hosted playback, or paste a YouTube / Facebook / external MP4 link.
                        </p>
                      </div>
                    </>
                  )}

                  {adImagePreview && adForm.mediaType === 'image' && (
                    <div className="adm-img-preview-wrap">
                      <img src={adImagePreview} alt="Ad preview" />
                    </div>
                  )}

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Headline (Telugu)</label>
                      <input type="text" value={adForm.title} onChange={(e) => setAdForm((f) => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div className="adm-field">
                      <label>Headline (English)</label>
                      <input type="text" value={adForm.titleEn} onChange={(e) => setAdForm((f) => ({ ...f, titleEn: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Description (Telugu)</label>
                      <textarea rows={3} value={adForm.content} onChange={(e) => setAdForm((f) => ({ ...f, content: e.target.value }))} />
                    </div>
                    <div className="adm-field">
                      <label>Description (English)</label>
                      <textarea rows={3} value={adForm.contentEn} onChange={(e) => setAdForm((f) => ({ ...f, contentEn: e.target.value }))} />
                    </div>
                  </div>

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Button text (Telugu)</label>
                      <input type="text" value={adForm.buttonLabel} onChange={(e) => setAdForm((f) => ({ ...f, buttonLabel: e.target.value }))} placeholder="ఇప్పుడే చూడండి" />
                    </div>
                    <div className="adm-field">
                      <label>Button text (English)</label>
                      <input type="text" value={adForm.buttonLabelEn} onChange={(e) => setAdForm((f) => ({ ...f, buttonLabelEn: e.target.value }))} placeholder="Learn more" />
                    </div>
                  </div>

                  <div className="adm-row">
                    <div className="adm-field">
                      <label>Button link (URL)</label>
                      <input type="url" value={adForm.buttonUrl} onChange={(e) => setAdForm((f) => ({ ...f, buttonUrl: e.target.value }))} placeholder="https://..." />
                    </div>
                    <div className="adm-field">
                      <label>Sponsor label</label>
                      <input type="text" value={adForm.sponsorLabel} onChange={(e) => setAdForm((f) => ({ ...f, sponsorLabel: e.target.value }))} placeholder="Sponsored · Brand name" />
                    </div>
                  </div>

                  <label className="adm-checkbox">
                    <input type="checkbox" checked={adForm.active} onChange={(e) => setAdForm((f) => ({ ...f, active: e.target.checked }))} />
                    Active (visible on website)
                  </label>

                  <div className="adm-form-actions">
                    <button type="submit" className="adm-btn adm-btn-primary" disabled={savingAd}>
                      {savingAd ? <><span className="adm-spinner" /> Saving…</> : (editingAdId ? 'Save changes' : 'Publish ad')}
                    </button>
                    {editingAdId && (
                      <button type="button" className="adm-btn adm-btn-ghost" onClick={resetAdForm}>Cancel</button>
                    )}
                  </div>
                </form>
              </div>

              {[
                { title: 'Sidebar ads', hint: 'First 2 active ads in the right column. Image: 720×405px (16:9). Drag to reorder.', list: orderedSidebarAds, placement: 'sidebar', setter: setOrderedSidebarAds },
                { title: 'Top partner banners', hint: 'Shown at the top of the homepage. Logo image: 640×144px (wide). Drag to reorder.', list: orderedBannerAds, placement: 'banner', setter: setOrderedBannerAds },
              ].map(({ title, hint, list, placement, setter }) => (
                <div className="adm-card" key={placement}>
                  <div className="adm-card-head">
                    <div><h3>{title}</h3><p className="adm-card-sub">{hint}</p></div>
                    <span className="adm-pill">{list.length}</span>
                  </div>
                  {list.length === 0 ? (
                    <div className="adm-empty"><div className="adm-empty-icon">◎</div>No ads in this slot yet.</div>
                  ) : (
                    <div className="adm-list adm-list-sortable">
                      {list.map((ad) => (
                        <div
                          key={ad.id}
                          className={`adm-list-item adm-list-item-draggable ${dragAdId.current === ad.id ? 'is-dragging' : ''}`}
                          draggable
                          onDragStart={() => { dragAdId.current = ad.id; }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const fromId = dragAdId.current;
                            dragAdId.current = null;
                            const next = reorderAdList(list, fromId, ad.id);
                            if (next) {
                              setter(next);
                              persistAdOrder(next, placement);
                            }
                          }}
                          onDragEnd={() => { dragAdId.current = null; }}
                        >
                          <span className="adm-drag-handle" title="Drag to reorder">⋮⋮</span>
                          {ad.image && (
                            <img src={ad.image.startsWith('http') ? ad.image : `${API}${ad.image}`} alt="" className="adm-story-thumb" />
                          )}
                          {!ad.image && ad.mediaType === 'video' && ad.videoUrl && (
                            <span className="adm-ad-video-thumb" aria-hidden>🎬</span>
                          )}
                          <div className="adm-list-body">
                            <div className="adm-list-title">{ad.title || ad.titleEn || '(No title)'}</div>
                            <div className="adm-list-sub">
                              <span className="adm-tag">{ad.mediaType}</span>
                              {!ad.active && <span className="adm-list-meta">Inactive</span>}
                            </div>
                          </div>
                          <div className="adm-list-actions">
                            <button type="button" className="adm-btn adm-btn-ghost" onClick={() => startEditAd(ad)}>Edit</button>
                            <button type="button" className="adm-btn adm-btn-danger" onClick={() => handleDeleteAd(ad.id)} disabled={deletingAd === ad.id}>
                              {deletingAd === ad.id ? <span className="adm-spinner dark" /> : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {tab === 'poll' && (
            <>
              {pollLoading ? (
                <div className="adm-card">
                  <div className="adm-loading-block"><span className="adm-spinner dark" /> Loading poll…</div>
                </div>
              ) : currentPoll && (
                <div className="adm-card">
                  <div className="adm-card-head">
                    <div><h3>Live poll on site</h3><p>Current question readers are voting on.</p></div>
                    <button type="button" className="adm-btn adm-btn-ghost adm-btn-sm" onClick={fetchPoll}>Refresh</button>
                  </div>
                  <div className="adm-poll-live">
                    <h4>Active question</h4>
                    <p className="adm-poll-live-q">{currentPoll.question}</p>
                    <div className="adm-poll-results">
                      {(currentPoll.options || []).map((opt) => {
                        const total = (currentPoll.options || []).reduce((s, o) => s + (o.votes || 0), 0);
                        const pct = total ? Math.round(((opt.votes || 0) / total) * 100) : 0;
                        return (
                          <div key={opt.id} className="adm-poll-result-row">
                            <span className="adm-poll-result-label">{opt.text}</span>
                            <div className="adm-poll-result-bar-wrap">
                              <div className="adm-poll-result-bar" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="adm-poll-result-votes">{opt.votes || 0}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div className="adm-card">
                <div className="adm-card-head">
                  <div>
                    <h3>{currentPoll ? 'Replace poll' : 'Create poll'}</h3>
                    <p>Saving a new poll resets all vote counts. Minimum 2 answer options.</p>
                  </div>
                </div>
                <form onSubmit={handleSavePoll} className="adm-form">
                  <div className="adm-field">
                    <label>Question *</label>
                    <input type="text" value={pollQ} onChange={(e) => setPollQ(e.target.value)} placeholder="What would you like to ask readers?" required />
                  </div>
                  {pollOpts.map((opt, i) => (
                    <div className="adm-field" key={i}>
                      <label>Option {i + 1}{i < 2 ? ' *' : ' (optional)'}</label>
                      <input type="text" value={opt} onChange={(e) => { const o = [...pollOpts]; o[i] = e.target.value; setPollOpts(o); }} placeholder={`Answer ${i + 1}`} />
                    </div>
                  ))}
                  <button type="submit" className="adm-btn adm-btn-primary" disabled={savingPoll}>
                    {savingPoll ? <><span className="adm-spinner" /> Saving…</> : (currentPoll ? 'Replace poll' : 'Publish poll')}
                  </button>
                </form>
              </div>
            </>
          )}

          {tab === 'breaking' && (
            <>
              <div className="adm-card">
                <div className="adm-card-head">
                  <div><h3>Add headline</h3><p>Shown in the scrolling ticker on the homepage.</p></div>
                </div>
                <form onSubmit={handleAddHeadline} className="adm-form">
                  <div className="adm-field">
                    <label>Headline *</label>
                    <input type="text" value={newHeadline} onChange={(e) => setNewHeadline(e.target.value)} placeholder="Breaking news text…" required />
                  </div>
                  <button type="submit" className="adm-btn adm-btn-primary" disabled={savingHeadline}>
                    {savingHeadline ? <><span className="adm-spinner" /> Adding…</> : 'Add to ticker'}
                  </button>
                </form>
              </div>
              {breakingNews.length > 0 && (
                <div className="adm-card">
                  <div className="adm-card-head">
                    <div><h3>Active headlines</h3></div>
                    <span className="adm-pill">{breakingNews.length}</span>
                  </div>
                  <div className="adm-list">
                    {breakingNews.map((h, i) => (
                      <div key={h.id} className="adm-list-item">
                        <div className="adm-list-icon">⚡</div>
                        <div className="adm-list-body">
                          <div className="adm-list-title">{h.headline}</div>
                        </div>
                        <button type="button" className="adm-btn adm-btn-danger" onClick={() => handleDeleteHeadline(h.id)} disabled={deletingHeadline === h.id}>
                          {deletingHeadline === h.id ? <span className="adm-spinner dark" /> : 'Delete'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          </main>
        </div>
      </div>
    </div>
  );
}
